import React from 'react'
import { EMkStepStatus } from '@typescript'
import type { TMkStep, IMkInputField } from '@typescript'
import { formatDateRangeFromStrings } from '@utils/formatDateRange'
import { SUITES_SELECTED_LABEL } from '@pages/account/Waitlist/constants'
import type { IWaitlistState, IWaitlistDraft, IWaitlistStep, WaitlistAction } from '../../types'
import { EWaitlistStepName } from '../../types'
import type { WaitlistContextType } from '../../contexts/waitlist/_context'
import * as actions from '../../store/waitlist/_actions'

// TODO: Remove this once the real API is implemented
import waitlistDraftMock from '../../../../../../mkback/src/main/resources/content/waitlist/ebac-waitlist-draft.json'

/**
 * TODO: Replace with real API;
 * Simulates the waitlist step API: no network call, no mock/fixture data.
 * Takes the current draft and returns a new draft with step statuses advanced
 * (completed step → COMPLETE, next → ACTIVE). Used until a real backend
 * implements the contract.
 */
function executeStepWaitlistRequestMock(completedStepName: string, draft: IWaitlistDraft): Promise<IWaitlistDraft> {
  const steps = [...(draft.steps || [])]
  const completedIndex = steps.findIndex((s) => s.name === completedStepName)
  if (completedIndex === -1) return Promise.resolve({ ...draft, steps })

  steps[completedIndex] = { ...steps[completedIndex], status: EMkStepStatus.COMPLETE }
  const nextIndex = completedIndex + 1
  if (nextIndex < steps.length) {
    steps[nextIndex] = { ...steps[nextIndex], status: EMkStepStatus.ACTIVE }
  }
  return Promise.resolve({ ...draft, steps, errors: [] })
}

export class WaitlistService {
  private state: IWaitlistState

  private dispatch: React.Dispatch<WaitlistAction>

  constructor(state: IWaitlistState, dispatch: React.Dispatch<WaitlistAction>) {
    this.state = state
    this.dispatch = dispatch
  }

  getContextProps(): WaitlistContextType {
    return {
      waitlistDraft: this.state.waitlistDraft,
      isLoading: this.state.isLoading,
      waitlistErrors: this.state.waitlistErrors ?? [],

      checkStepStatus: WaitlistService.checkStepStatus,
      findStepIndex: this.findStepIndexByName.bind(this),
      getActiveStep: this.getActiveStep.bind(this),
      handleStepComplete: this.handleStepComplete.bind(this),
      handleStepEdit: this.handleStepEdit.bind(this),
      handleStepInputFieldChange: this.handleStepInputFieldChange.bind(this),
      initWaitlistCreate: this.initWaitlistCreate.bind(this),
      submitWaitlistCreate: this.submitWaitlistCreate.bind(this),
      resetWaitlistCreate: this.resetWaitlistCreate.bind(this),
    }
  }

  resetWaitlistCreate(): void {
    this.dispatch(actions.clearWaitlistDraft())
  }

  static checkStepStatus(step: TMkStep, status: EMkStepStatus): boolean {
    return [status, status?.toString()].includes(step.status)
  }

  private static getSelectOptionLabel(step: IWaitlistStep, fieldName: string): string {
    const value = step.formState?.[fieldName]
    if (value == null || value === '') return ''
    const options = step.fieldSet?.[fieldName]?.options
    const option = Array.isArray(options) ? options.find((o: { value: string }) => String(o.value) === String(value)) : undefined
    return option && 'label' in option && option.label ? String(option.label) : ''
  }

  private static getCheckedCheckboxLabels(step: IWaitlistStep): string[] {
    const { fieldSet = {}, formState = {} } = step
    return Object.values(fieldSet)
      .filter((f) => f?.type === 'checkbox' && formState[f.name])
      .map((f) => ('label' in f ? String(f.label) : ''))
      .filter(Boolean)
  }

  findStepIndexByName(stepName: string): number {
    const index = this.state.waitlistDraft?.steps.findIndex((s) => s.name === stepName)
    return index ?? -1
  }

  getActiveStep(): IWaitlistStep | undefined {
    const steps = this.state.waitlistDraft?.steps
    if (!steps) return undefined
    return steps.find((step) => WaitlistService.checkStepStatus(step, EMkStepStatus.ACTIVE)) as IWaitlistStep | undefined
  }

  async handleStepInputFieldChange(step: IWaitlistStep, fieldName: string, fieldValue: IMkInputField['value'], fieldType: string, opts?: { checked?: boolean }): Promise<void> {
    const valueToUse = fieldType === 'checkbox' && !opts?.checked ? '' : fieldValue
    this.dispatch(actions.updateField(step, fieldName, valueToUse, fieldType))
  }

  handleStepEdit(step: TMkStep): void {
    const steps = this.state.waitlistDraft?.steps
    if (!steps) return
    const stepIndex = this.findStepIndexByName(step.name)
    if (stepIndex === -1) return

    const updatedSteps = steps.map((currentStep, index) => {
      let status: string
      if (index === stepIndex) {
        status = EMkStepStatus.ACTIVE
        return {
          ...currentStep,
          status,
          formState: { ...currentStep.formState, titleCompleted: currentStep.title },
        }
      }
      if (index < stepIndex) status = EMkStepStatus.COMPLETE
      else status = EMkStepStatus.INCOMPLETE
      return { ...currentStep, status }
    }) as IWaitlistStep[]

    const updatedDraft: IWaitlistDraft = {
      ...this.state.waitlistDraft!,
      steps: updatedSteps,
    }
    this.dispatch(actions.setWaitlistDraft(updatedDraft))
  }

  async handleStepComplete(): Promise<void> {
    const draft = this.state.waitlistDraft
    const activeStep = this.getActiveStep()

    if (!draft?.steps?.length || !activeStep) {
      return
    }

    const activeStepIndex = this.findStepIndexByName(activeStep.name)
    const isLastStep = activeStepIndex >= 0 && activeStepIndex === draft.steps.length - 1
    if (isLastStep) {
      return
    }

    this.dispatch(actions.setWaitlistLoading(true))
    this.dispatch(actions.clearWaitlistError())

    try {
      // TODO: Replace with real API;
      const newDraft = await executeStepWaitlistRequestMock(activeStep.name, draft)

      if (Array.isArray(newDraft.errors) && newDraft.errors.length > 0) {
        const first = newDraft.errors[0]
        const message = typeof first === 'object' && first && 'message' in first ? String(first.message) : String(first)
        this.dispatch(actions.setWaitlistError({ name: 'WaitlistError', message, raw: new Error(message) }))
        return
      }

      this.dispatch(actions.setWaitlistDraft(newDraft))

      const completedStep = newDraft.steps.find((s) => s.name === activeStep.name)
      if (completedStep) {
        this.setStepTitleCompleted(completedStep)
      }
    } catch (error) {
      const message = error instanceof Error ? error.message : String(error)
      this.dispatch(actions.setWaitlistError({ name: 'WaitlistError', message, raw: error instanceof Error ? error : new Error(message) }))
    } finally {
      this.dispatch(actions.setWaitlistLoading(false))
    }
  }

  async initWaitlistCreate(): Promise<void> {
    this.dispatch(actions.setWaitlistLoading(true))
    this.dispatch(actions.clearWaitlistError())
    try {
      // TODO: replace with real API when backend provides waitlist draft endpoint
      const draft = waitlistDraftMock as unknown as IWaitlistDraft
      this.dispatch(actions.setWaitlistDraft(draft))
    } catch (error) {
      const message = error instanceof Error ? error.message : String(error)
      this.dispatch(actions.setWaitlistError({ name: 'WaitlistError', message, raw: error instanceof Error ? error : new Error(message) }))
    } finally {
      this.dispatch(actions.setWaitlistLoading(false))
    }
  }

  // TODO: Replace with real API;
  async submitWaitlistCreate(): Promise<void> {
    const draft = this.state.waitlistDraft
    if (!draft?.steps?.length) return

    this.dispatch(actions.setWaitlistLoading(true))
    this.dispatch(actions.clearWaitlistError())
    try {
      // TODO: Replace with real API
      await new Promise<void>((resolve) => {
        setTimeout(resolve, 800)
      })
    } catch (error) {
      const message = error instanceof Error ? error.message : String(error)
      this.dispatch(actions.setWaitlistError({ name: 'WaitlistError', message, raw: error instanceof Error ? error : new Error(message) }))
      throw error
    } finally {
      this.dispatch(actions.setWaitlistLoading(false))
    }
  }

  private updateStepCompletedTitleState(step: IWaitlistStep, title: string): void {
    this.dispatch(actions.updateField(step, 'titleCompleted', title, 'text'))
  }

  private resetStepTitleCompleted(step: IWaitlistStep): void {
    this.updateStepCompletedTitleState(step, step.title)
  }

  private setStepTitleCompleted(step: IWaitlistStep): void {
    if (!step?.name) return
    switch (step.name) {
      case EWaitlistStepName.WAITLIST_RESORT:
        this.setStepTitleCompletedResort(step)
        break
      case EWaitlistStepName.WAITLIST_DATES:
        this.setStepTitleCompletedDates(step)
        break
      case EWaitlistStepName.WAITLIST_SUITES:
        this.setStepTitleCompletedSuites(step)
        break
      case EWaitlistStepName.WAITLIST_CONTACT:
        // No completed-title method needed;
        break
      default:
        break
    }
  }

  private setStepTitleCompletedResort(step: IWaitlistStep): void {
    const label = WaitlistService.getSelectOptionLabel(step, 'resort')
    this.updateStepCompletedTitleState(step, label || step.title)
  }

  private setStepTitleCompletedDates(step: IWaitlistStep): void {
    const checkIn = String(step.formState?.checkInDate ?? '').trim()
    const checkOut = String(step.formState?.checkOutDate ?? '').trim()
    const dateRange = checkIn && checkOut ? formatDateRangeFromStrings(checkIn, checkOut) : ''
    const minL = WaitlistService.getSelectOptionLabel(step, 'minNights')
    const maxL = WaitlistService.getSelectOptionLabel(step, 'maxNights')
    const nights = minL && maxL && minL !== maxL ? `${minL} – ${maxL}` : minL || maxL
    this.updateStepCompletedTitleState(step, dateRange || nights || step.title)
  }

  private setStepTitleCompletedSuites(step: IWaitlistStep): void {
    const { fieldSet = {}, formState = {} } = step
    const suiteCheckboxKeys = Object.entries(fieldSet)
      .filter(([, fieldS]) => fieldS?.type === 'checkbox')
      .map(([key]) => key)
    const count = suiteCheckboxKeys.filter((key) => formState[key]).length
    const title = count > 0 ? `[${count}] ${SUITES_SELECTED_LABEL}` : step.title
    this.updateStepCompletedTitleState(step, title)
  }
}
